import { HERO_HEADER_VIEW, type HeroHeaderView } from "@opensea/navigation"
import { useSelectedLayoutSegments } from "next/navigation"
import { useShallow } from "zustand/react/shallow"
import { topNavbarVariantsForHeroHeaderView } from "@/components/hero-header/utils"
import { useTopNavbarStore } from "@/stores/use-top-navbar-store"
import type {
  TopNavbarTransparency,
  TopNavbarVariants,
} from "./TopNavbarContainer"

const SEGMENT_TO_TOP_NAVBAR_TRANSPARENCY: Record<
  string,
  TopNavbarTransparency
> = {
  "(default-nav)": "none",
  "(full-immersive)": "default",
  "(hidden-nav)": "hidden",
}

export function useTopNavbarVariants({ isMobile }: { isMobile: boolean }) {
  const segments = useSelectedLayoutSegments()
  const { variants: ctxVariants } = useTopNavbarStore(
    useShallow(state => ({ variants: state.variants })),
  )

  function getVariants(): TopNavbarVariants {
    if (!segments) {
      return {
        transparent: "none",
      }
    }

    /**
     * Studio pages should not have a border
     */
    const isStudio =
      segments.includes("studio") && !segments.includes("(hidden-nav)")
    if (isStudio) {
      return {
        transparent: "none",
        border: false,
      }
    }

    const isDiscover = segments.includes("discover")
    const heroHeaderView = segments.find(
      segment => segment in HERO_HEADER_VIEW,
    ) as HeroHeaderView | undefined

    if (isMobile && (isDiscover || heroHeaderView)) {
      return {
        transparent: "default",
        dark: "topOnly",
        gradient: "topOnly",
      }
    }

    if (segments.includes("preview") && segments.includes("collection")) {
      return {
        transparent: "invisible",
      }
    }

    if (ctxVariants) {
      return ctxVariants
    }

    if (heroHeaderView) {
      return topNavbarVariantsForHeroHeaderView(heroHeaderView)
    }

    return {
      transparent: SEGMENT_TO_TOP_NAVBAR_TRANSPARENCY[segments[0]],
    }
  }

  return getVariants()
}
